import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import {
  UsersIcon, FolderIcon, ClipboardDocumentListIcon, CurrencyDollarIcon,
  ArrowTrendingUpIcon, ClockIcon, CheckCircleIcon, ExclamationTriangleIcon,
  ChartBarIcon, PlusIcon, CalendarIcon, BuildingOfficeIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline';
import {
  Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale,
  LinearScale, BarElement
} from 'chart.js';
import { Doughnut } from 'react-chartjs-2';
import { PageHeader } from '../../components/layout';
import {
  Card, CardBody, CardHeader, CardTitle, StatusBadge, PriorityBadge,
  ProgressBar, PageLoader, Avatar, Badge, Button, EmptyState
} from '../../components/ui';
import { reportService } from '../../services';
import { useAuthStore } from '../../stores';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';

dayjs.extend(relativeTime);

ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement);

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDashboardStats();
  }, []);

  const fetchDashboardStats = async () => {
    try {
      const data = await reportService.getDashboardStats();
      setStats(data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  const chartColors = {
    primary: '#1D4ED8',
    secondary: '#14B8A6',
    accent: '#FBBF24',
    success: '#10B981',
    danger: '#EF4444',
    warning: '#F59E0B',
    neutral: '#6B7280'
  };

  // Project Status Chart Data
  const getProjectStatusData = () => {
    if (!stats?.projects?.byStatus) return null;

    const statusData = stats.projects.byStatus;
    const keys = Object.keys(statusData);

    // If no keys or all values are 0, return null
    if (keys.length === 0) return null;

    const labels = keys.map(key => {
      // Handle status keys with underscores (e.g., 'in_progress' -> 'inProgress')
      const statusKey = key.replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
      return t(`projects.statuses.${statusKey}`) || t(`projects.statuses.${key}`) || key;
    });
    const data = keys.map(key => statusData[key]);

    // Check if there's any data to display
    const total = data.reduce((sum, val) => sum + val, 0);
    if (total === 0) return null;

    return {
      labels,
      datasets: [{
        data,
        backgroundColor: [
          chartColors.neutral,
          chartColors.success,
          chartColors.accent,
          chartColors.primary,
          chartColors.danger,
          chartColors.warning,
          chartColors.secondary
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  // Task Status Chart Data
  const getTaskStatusData = () => {
    if (!stats?.tasks) return null;
    const completed = stats.tasks.completed || 0;
    const overdue = stats.tasks.overdue || 0;
    const total = stats.tasks.total || 0;
    const pending = Math.max(0, total - completed - overdue);

    return {
      labels: [
        t('tasks.statuses.completed'),
        t('tasks.overdue'),
        t('tasks.statuses.todo')
      ],
      datasets: [{
        data: [completed, overdue, pending],
        backgroundColor: [
          chartColors.success,
          chartColors.danger,
          chartColors.neutral
        ],
        borderWidth: 2,
        borderColor: '#fff'
      }]
    };
  };

  // Different stat cards for clients vs regular users
  const statCards = user?.role === 'client' ? [
    {
      title: t('dashboard.totalProjects') || 'Total Projects',
      value: stats?.projects?.total || 0,
      change: `${stats?.projects?.active || 0} ${t('dashboard.active') || 'Active'}`,
      icon: FolderIcon,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      link: '/projects',
      trend: 'neutral'
    },
    {
      title: t('dashboard.completedProjects') || 'Completed',
      value: stats?.projects?.byStatus?.completed || 0,
      change: `${stats?.projects?.byStatus?.active || 0} ${t('dashboard.inProgress') || 'In Progress'}`,
      icon: CheckCircleIcon,
      color: 'text-success',
      bgColor: 'bg-success/10',
      link: '/projects?status=completed',
      trend: 'neutral'
    },
    {
      title: t('dashboard.activeProjects') || 'Active Projects',
      value: stats?.projects?.active || 0,
      change: `${stats?.projects?.byStatus?.on_hold || 0} ${t('dashboard.onHold') || 'On Hold'}`,
      icon: FolderIcon,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      link: '/projects?status=active',
      trend: 'neutral'
    },
    {
      title: t('dashboard.progress') || 'Average Progress',
      value: `${Math.round((stats?.recentProjects?.reduce((sum, p) => sum + (p.progress || 0), 0) / (stats?.recentProjects?.length || 1)) || 0)}%`,
      change: `${stats?.recentProjects?.length || 0} ${t('dashboard.recentProjects') || 'Recent Projects'}`,
      icon: ChartBarIcon,
      color: 'text-accent',
      bgColor: 'bg-accent/10',
      link: '/projects',
      trend: 'neutral'
    }
  ] : [
    {
      title: t('dashboard.totalClients'),
      value: stats?.clients?.total || 0,
      change: `+${stats?.clients?.newThisMonth || 0} ${t('dashboard.thisMonth')}`,
      icon: UsersIcon,
      color: 'text-primary',
      bgColor: 'bg-primary/10',
      link: '/clients',
      trend: stats?.clients?.newThisMonth > 0 ? 'up' : 'neutral'
    },
    {
      title: t('dashboard.activeProjects'),
      value: stats?.projects?.active || 0,
      change: `${stats?.projects?.total || 0} ${t('dashboard.total')}`,
      icon: FolderIcon,
      color: 'text-secondary',
      bgColor: 'bg-secondary/10',
      link: '/projects',
      trend: 'neutral'
    },
    {
      title: t('dashboard.pendingTasks'),
      value: (stats?.tasks?.total || 0) - (stats?.tasks?.completed || 0),
      change: `${stats?.tasks?.overdue || 0} ${t('dashboard.overdueTasks')}`,
      icon: ClipboardDocumentListIcon,
      color: stats?.tasks?.overdue > 0 ? 'text-danger' : 'text-accent',
      bgColor: stats?.tasks?.overdue > 0 ? 'bg-danger/10' : 'bg-accent/10',
      link: '/tasks',
      trend: stats?.tasks?.overdue > 0 ? 'down' : 'neutral',
      warning: stats?.tasks?.overdue > 0
    },
    {
      title: t('dashboard.totalRevenue'),
      value: `$${(stats?.invoices?.totalRevenue || 0).toLocaleString()}`,
      change: `$${(stats?.invoices?.outstanding || 0).toLocaleString()} ${t('dashboard.outstanding')}`,
      icon: CurrencyDollarIcon,
      color: 'text-success',
      bgColor: 'bg-success/10',
      link: '/invoices',
      trend: 'neutral'
    }
  ];

  const quickActions = [
    { label: t('projects.newProject'), icon: PlusIcon, link: '/projects/new', color: 'primary' },
    { label: t('tasks.newTask'), icon: PlusIcon, link: '/tasks/new', color: 'secondary' },
    { label: t('clients.newClient'), icon: PlusIcon, link: '/clients/new', color: 'accent' },
    { label: t('invoices.newInvoice'), icon: PlusIcon, link: '/invoices/new', color: 'success' }
  ];

  const isOverdue = (task) => {
    return task.dueDate && dayjs(task.dueDate).isBefore(dayjs(), 'day') && task.status !== 'completed';
  };

  return (
    <>
      <Helmet>
        <title>{t('dashboard.title')} - ProCTM</title>
      </Helmet>

      <PageHeader
        // title={`${t('dashboard.welcome')} ${user?.firstName}`}
        title={`${t('dashboard.title')}`}
        description={user?.role === 'client' ? (t('dashboard.clientOverview') || 'View your projects and track progress') : t('dashboard.overview')}
        actions={
          user?.role !== 'client' && (
            <div className="flex items-center gap-2">
              <Button variant="outline" icon={ChartBarIcon} onClick={() => navigate('/reports')}>
                {t('reports.title')}
              </Button>
            </div>
          )
        }
      />

      {/* Quick Actions */}
      <Card className="mb-6">
        <CardBody>
          <div className="flex items-center gap-4 flex-wrap">
            <span className="text-sm font-medium text-gray-700">{t('dashboard.quickActions') || 'Quick Actions'}:</span>
            {quickActions.map((action, index) => (
              <Button
                key={index}
                variant="outline"
                size="sm"
                icon={action.icon}
                onClick={() => navigate(action.link)}
                className="flex items-center gap-2"
              >
                {action.label}
              </Button>
            ))}
          </div>
        </CardBody>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        {statCards.map((stat, index) => (
          <Card key={index} className="hover:shadow-lg transition-shadow cursor-pointer" onClick={() => navigate(stat.link)}>
            <CardBody className="p-6">
              <div className="flex items-center justify-between mb-4">
                <div className={`p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-6 h-6 ${stat.color}`} />
                </div>
                {stat.trend === 'up' && (
                  <ArrowTrendingUpIcon className="w-5 h-5 text-success" />
                )}
                {stat.warning && (
                  <ExclamationTriangleIcon className="w-5 h-5 text-danger" />
                )}
              </div>
              <div>
                <p className="text-sm text-gray-500 mb-1">{stat.title}</p>
                <p className={`text-3xl font-bold ${stat.color} mb-1`}>{stat.value}</p>
                <p className={`text-xs ${stat.warning ? 'text-danger font-medium' : 'text-gray-400'}`}>
                  {stat.change}
                </p>
              </div>
            </CardBody>
          </Card>
        ))}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Project Status Chart */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <ChartBarIcon className="w-5 h-5 text-primary" />
              {t('dashboard.projectsByStatus') || 'Projects by Status'}
            </CardTitle>
          </CardHeader>
          <CardBody>
            {getProjectStatusData() ? (
              <div className="h-64">
                <Doughnut
                  data={getProjectStatusData()}
                  options={{
                    maintainAspectRatio: false,
                    responsive: true,
                    plugins: {
                      legend: {
                        position: 'bottom',
                        labels: {
                          padding: 15,
                          usePointStyle: true
                        }
                      },
                      tooltip: {
                        callbacks: {
                          label: (context) => {
                            const total = context.dataset.data.reduce((a, b) => a + b, 0);
                            const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                            return `${context.label}: ${context.parsed} (${percentage}%)`;
                          }
                        }
                      }
                    }
                  }}
                />
              </div>
            ) : (
              <EmptyState
                icon={ChartBarIcon}
                title={t('dashboard.noProjectData') || 'No Project Data'}
                description={t('dashboard.noProjectDataDesc') || 'Create projects to see status distribution'}
                className="py-12"
              />
            )}
          </CardBody>
        </Card>

        {user?.role === 'client' && (
          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FolderIcon className="w-5 h-5 text-primary" />
                {t('dashboard.recentProjects')}
              </CardTitle>
              <Link to="/projects" className="text-sm text-primary hover:underline flex items-center gap-1">
                {t('common.viewAll')}
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardBody className="p-0">
              {stats?.recentProjects?.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {stats.recentProjects.map((project) => (
                    <Link
                      key={project._id}
                      to={`/projects/${project._id}`}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-semibold text-neutral-dark truncate group-hover:text-primary transition-colors">
                            {project.name}
                          </p>
                          <StatusBadge status={project.status} />
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          {project.client && (
                            <div className="flex items-center gap-1">
                              <BuildingOfficeIcon className="w-4 h-4" />
                              <span>{project.client.name}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{dayjs(project.updatedAt).fromNow()}</span>
                          </div>
                        </div>
                        <div className="mt-2">
                          <ProgressBar value={project.progress || 0} size="sm" />
                          <span className="text-xs text-gray-500 mt-1">{project.progress || 0}% {t('common.complete')}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={FolderIcon}
                  title={t('projects.noProjects')}
                  description={user?.role === 'client'
                    ? (t('dashboard.noProjectsClientDesc') || 'No projects assigned yet')
                    : (t('dashboard.noProjectsDesc') || 'Create your first project to get started')
                  }
                  action={user?.role !== 'client' ? () => navigate('/projects/new') : undefined}
                  actionLabel={user?.role !== 'client' ? t('projects.newProject') : undefined}
                />
              )}
            </CardBody>
          </Card>
        )}

        {/* Task Status Chart - Only for non-client users */}
        {user?.role !== 'client' && (
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <ClipboardDocumentListIcon className="w-5 h-5 text-primary" />
                {t('dashboard.tasksByStatus') || 'Tasks by Status'}
              </CardTitle>
            </CardHeader>
            <CardBody>
              {getTaskStatusData() && (getTaskStatusData().datasets[0].data.reduce((a, b) => a + b, 0) > 0) ? (
                <div className="h-64">
                  <Doughnut
                    data={getTaskStatusData()}
                    options={{
                      maintainAspectRatio: false,
                      responsive: true,
                      plugins: {
                        legend: {
                          position: 'bottom',
                          labels: {
                            padding: 15,
                            usePointStyle: true
                          }
                        },
                        tooltip: {
                          callbacks: {
                            label: (context) => {
                              const total = context.dataset.data.reduce((a, b) => a + b, 0);
                              const percentage = total > 0 ? ((context.parsed / total) * 100).toFixed(1) : 0;
                              return `${context.label}: ${context.parsed} (${percentage}%)`;
                            }
                          }
                        }
                      }
                    }}
                  />
                </div>
              ) : (
                <EmptyState
                  icon={ClipboardDocumentListIcon}
                  title={t('dashboard.noTaskData') || 'No Task Data'}
                  description={t('dashboard.noTaskDataDesc') || 'Create tasks to see status distribution'}
                  className="py-12"
                />
              )}
            </CardBody>
          </Card>
        )}
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Recent Projects */}
        {user?.role !== 'client' && (
          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FolderIcon className="w-5 h-5 text-primary" />
                {t('dashboard.recentProjects')}
              </CardTitle>
              <Link to="/projects" className="text-sm text-primary hover:underline flex items-center gap-1">
                {t('common.viewAll')}
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardBody className="p-0">
              {stats?.recentProjects?.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {stats.recentProjects.map((project) => (
                    <Link
                      key={project._id}
                      to={`/projects/${project._id}`}
                      className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2">
                          <p className="font-semibold text-neutral-dark truncate group-hover:text-primary transition-colors">
                            {project.name}
                          </p>
                          <StatusBadge status={project.status} />
                        </div>
                        <div className="flex items-center gap-4 text-sm text-gray-500">
                          {project.client && (
                            <div className="flex items-center gap-1">
                              <BuildingOfficeIcon className="w-4 h-4" />
                              <span>{project.client.name}</span>
                            </div>
                          )}
                          <div className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            <span>{dayjs(project.updatedAt).fromNow()}</span>
                          </div>
                        </div>
                        <div className="mt-2">
                          <ProgressBar value={project.progress || 0} size="sm" />
                          <span className="text-xs text-gray-500 mt-1">{project.progress || 0}% {t('common.complete')}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <EmptyState
                  icon={FolderIcon}
                  title={t('projects.noProjects')}
                  description={user?.role === 'client'
                    ? (t('dashboard.noProjectsClientDesc') || 'No projects assigned yet')
                    : (t('dashboard.noProjectsDesc') || 'Create your first project to get started')
                  }
                  action={user?.role !== 'client' ? () => navigate('/projects/new') : undefined}
                  actionLabel={user?.role !== 'client' ? t('projects.newProject') : undefined}
                />
              )}
            </CardBody>
          </Card>
        )}

        {/* Upcoming Tasks */}
        {user?.role !== 'client' && (
          <Card>
            <CardHeader className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <ClipboardDocumentListIcon className="w-5 h-5 text-primary" />
                {t('dashboard.upcomingTasks')}
              </CardTitle>
              <Link to="/tasks" className="text-sm text-primary hover:underline flex items-center gap-1">
                {t('common.viewAll')}
                <ArrowRightIcon className="w-4 h-4" />
              </Link>
            </CardHeader>
            <CardBody className="p-0">
              {stats?.upcomingTasks?.length > 0 ? (
                <div className="divide-y divide-gray-100">
                  {stats.upcomingTasks.map((task) => {
                    const overdue = isOverdue(task);
                    return (
                      <Link
                        key={task._id}
                        to={`/tasks/${task._id}`}
                        className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors group"
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <p className={`font-semibold truncate group-hover:text-primary transition-colors ${task.status === 'completed' ? 'line-through text-gray-400' : 'text-neutral-dark'
                              }`}>
                              {task.title}
                            </p>
                            <PriorityBadge priority={task.priority} />
                            {overdue && (
                              <Badge variant="danger" className="text-xs">
                                {t('tasks.overdue')}
                              </Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-4 text-sm text-gray-500">
                            {task.project && (
                              <div className="flex items-center gap-1">
                                <FolderIcon className="w-4 h-4" />
                                <span>{task.project.name}</span>
                              </div>
                            )}
                            {task.dueDate && (
                              <div className={`flex items-center gap-1 ${overdue ? 'text-danger font-medium' : ''}`}>
                                <ClockIcon className="w-4 h-4" />
                                <span>
                                  {dayjs(task.dueDate).format('MMM D, YYYY')}
                                  {overdue && ' ⚠️'}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </Link>
                    );
                  })}
                </div>
              ) : (
                <EmptyState
                  icon={ClipboardDocumentListIcon}
                  title={t('tasks.noTasks')}
                  description={t('dashboard.noTasksDesc') || 'No upcoming tasks. Great job!'}
                  action={() => navigate('/tasks/new')}
                  actionLabel={t('tasks.newTask')}
                />
              )}

            </CardBody>
          </Card>
        )}

      </div>

      {/* Recent Clients - Only for non-client users */}
      {user?.role !== 'client' && (
        <Card>
          <CardHeader className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <UsersIcon className="w-5 h-5 text-primary" />
              {t('dashboard.recentClients')}
            </CardTitle>
            <Link to="/clients" className="text-sm text-primary hover:underline flex items-center gap-1">
              {t('common.viewAll')}
              <ArrowRightIcon className="w-4 h-4" />
            </Link>
          </CardHeader>
          <CardBody>
            {stats?.recentClients?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {stats.recentClients.map((client) => (
                  <Link
                    key={client._id}
                    to={`/clients/${client._id}`}
                    className="p-4 border border-gray-200 rounded-lg hover:border-primary hover:shadow-md transition-all group"
                  >
                    <div className="flex items-center gap-3 mb-3">
                      <Avatar name={client.name} src={client.photo ? (client.photo.startsWith('http') ? client.photo : process.env.REACT_APP_SOCKET_URL + client.photo) : null} size="md" />
                      <div className="flex-1 min-w-0">
                        <p className="font-semibold text-neutral-dark truncate group-hover:text-primary transition-colors">
                          {client.name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{client.email || '-'}</p>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <StatusBadge status={client.status} />
                      <span className="text-xs text-gray-400">
                        {dayjs(client.createdAt).fromNow()}
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <EmptyState
                icon={UsersIcon}
                title={t('clients.noClients')}
                description={t('dashboard.noClientsDesc') || 'Add your first client to start managing relationships'}
                action={() => navigate('/clients/new')}
                actionLabel={t('clients.newClient')}
              />
            )}
          </CardBody>
        </Card>
      )}
    </>
  );
}